import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import type { Block, LegalDoc } from "@/content/legal/types";

/**
 * Renders a structured legal document (src/content/legal) as a public marketing page: the
 * privacy policy (/privacy) and the biometric data retention policy (/biometric-data-retention).
 */
const linkClass = "text-hawk-orange underline hover:no-underline";

export const renderBlock = (block: Block, i: number) => {
  switch (block.type) {
    case "p":
      return (
        <p key={i} className="text-muted-foreground leading-relaxed">
          {block.text}
        </p>
      );
    case "link":
      return (
        <p key={i} className="text-muted-foreground leading-relaxed">
          {block.text}{" "}
          {block.href.startsWith("/") ? (
            <Link to={block.href} className={linkClass}>
              {block.label}
            </Link>
          ) : (
            <a href={block.href} className={linkClass}>
              {block.label}
            </a>
          )}
        </p>
      );
    case "ul":
      return (
        <ul key={i} className="list-disc pl-6 space-y-2 text-muted-foreground">
          {block.items.map((item, j) => (
            <li key={j} className="leading-relaxed">
              {item}
            </li>
          ))}
        </ul>
      );
    case "table":
      return (
        <div key={i} className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-border text-left">
                {block.head.map((h, j) => (
                  <th key={j} className="py-2 pr-4 font-semibold align-top">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, r) => (
                <tr key={r} className="border-b border-border/60 align-top">
                  {row.map((cell, c) => (
                    <td key={c} className="py-2 pr-4 text-muted-foreground">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    default:
      return null;
  }
};

interface Props {
  doc: LegalDoc;
  /** A closing "see also" link (e.g. the face data policy, from /privacy and /biometric-data-retention). */
  seeAlso?: { text: string; label: string; href: string };
  /** <title> / meta description; omitted = the page keeps the site defaults. */
  seo?: { title: string; description: string };
}

const LegalDocView = ({ doc, seo, seeAlso }: Props) => (
  <div className="min-h-screen bg-background">
    {seo && (
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
      </Helmet>
    )}
    <AppHeader variant="marketing" />
    <main className="container mx-auto px-4 pt-28 pb-20 max-w-3xl">
      <h1 className="text-3xl md:text-4xl font-display font-bold mb-2">{doc.title}</h1>
      <p className="text-sm text-muted-foreground mb-8">
        {doc.effectiveDate} · v{doc.version}
      </p>
      {doc.intro && (
        <p className="text-muted-foreground leading-relaxed border-l-2 border-border pl-4 mb-10">{doc.intro}</p>
      )}
      <div className="space-y-10">
        {doc.sections.map((section, s) => (
          <section key={s} className="space-y-3">
            <h2 className="text-xl font-semibold">{section.heading}</h2>
            {section.blocks.map(renderBlock)}
          </section>
        ))}
        {seeAlso && (
          <aside className="border-t border-border pt-6" data-testid="legal-see-also">
            {renderBlock({ type: "link", ...seeAlso }, 0)}
          </aside>
        )}
      </div>
    </main>
    <Footer />
  </div>
);

export default LegalDocView;
